/**
 * Service functions for handling player substitutions in matches
 */

import { buildLiveCommandAttemptData, pauseGame, resumeGame } from './simulationControlService';

/**
 * Converts display names to roster format (spaces replaced with underscores)
 * This ensures names match the format expected by the Python engine
 */
function formatPlayerNameForEngine(name) {
  if (!name || typeof name !== 'string') {
    return 'Unknown_Player';
  }
  return name.replace(/\s+/g, '_');
}

function getApiErrorMessage(error, fallback) {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback;
}

function isLineupLockedApiError(error) {
  return error?.response?.data?.code === 'MATCH_LINEUP_LOCKED';
}

function getSocketErrorDetails(error) {
  return error?.response || (error?.code ? error : undefined);
}

function notifyError(onError, message, details) {
  if (details !== undefined) {
    onError?.(message, details);
    return;
  }
  onError?.(message);
}

// A queued substitution can still fail when the engine drains its queue, so the
// result has to be waited for rather than assumed. The engine only drains on its
// next commentary event, which is normally a few seconds away.
export const QUEUED_SUBSTITUTION_RESULT_TIMEOUT_MS = 30000;

/**
 * The engine's absolute substitution total for a team, or null when the value
 * is missing or unusable.
 *
 * Mirrors the backend's toOptionalNonNegativeInt so both sides agree on what
 * counts as a real total. Booleans are rejected deliberately: `true` would
 * otherwise read as a count of one.
 */
function toEngineSubstitutionTotal(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  if (typeof rawValue === 'boolean') return null;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
}

/**
 * How many substitutions the team has used once this one has landed.
 *
 * Always prefer the engine's own absolute total. A paused engine emits a state
 * snapshot twice a second, each one carrying that total, so by the time this
 * result arrives the browser's counter may already include this very
 * substitution. Adding one on top of it is what left managers looking at
 * "1 of 3 left" after a single change, and cost them a substitution.
 *
 * The count derived from the browser is only a fallback for a response with no
 * total in it, and it is right only for as long as the browser's own count is:
 * a change made on another device, an injury the engine resolved by itself, or
 * a snapshot that landed early all break it.
 */
export function resolveSubstitutionsUsedTotal(commandResult, currentSubstitutionsUsed) {
  const engineTotal = toEngineSubstitutionTotal(
    commandResult?.substitutions_made ?? commandResult?.substitutionsMade
  );
  if (engineTotal !== null) {
    return engineTotal;
  }

  return (toEngineSubstitutionTotal(currentSubstitutionsUsed) ?? 0) + 1;
}

/**
 * Resolves with the engine's command_result for one queued substitution.
 *
 * Resolves to null on timeout rather than rejecting: a missing result is not an
 * error, it just means the UI has to wait for the next engine snapshot instead
 * of updating early. The listener always unsubscribes, on every exit path.
 */
export function waitForSubstitutionCommandResult({
  socketCtx,
  correlationId,
  timeoutMs = QUEUED_SUBSTITUTION_RESULT_TIMEOUT_MS
}) {
  if (!socketCtx || typeof socketCtx.subscribe !== 'function' || !correlationId) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};

    const settle = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(result);
    };

    const timer = setTimeout(() => settle(null), timeoutMs);

    unsubscribe = socketCtx.subscribe('command_result', (payload) => {
      if (payload?._correlationId !== correlationId) return;
      if (payload?.command_type && payload.command_type !== 'SUBSTITUTION') return;
      settle(payload);
    });

    // subscribe() can fire synchronously for an already-buffered result, in
    // which case settle() ran before this assignment and the listener above is
    // still attached. Drop it now.
    if (settled) {
      unsubscribe();
    }
  });
}

/**
 * Applies a queued substitution's outcome once the engine reports it.
 *
 * A timeout is not treated as a failure. The engine may simply be slow to reach
 * its next commentary event, and the persisted count comes from the engine
 * itself, so the next snapshot reconciles the UI regardless.
 */
export function watchQueuedSubstitution({ socketCtx, correlationId, onResolved, onRejected }) {
  return waitForSubstitutionCommandResult({ socketCtx, correlationId })
    .then((commandResult) => {
      if (!commandResult) return false;

      if (commandResult.success === true) {
        // Hand the payload on: it carries the engine's absolute substitution
        // total, which is the whole point of waiting for the queued result.
        onResolved?.(commandResult);
        return true;
      }

      onRejected?.(commandResult.message || 'The queued substitution could not be completed.');
      return false;
    })
    .catch((watchError) => {
      console.error('[SUBSTITUTION DEBUG] Failed while waiting for a queued substitution result:', watchError);
      return false;
    });
}

/**
 * Maps frontend position strings to engine format
 * Frontend uses: ST, CB, GK, CM, LW, RW, etc.
 * Engine expects concatenated format: GK, DFC, DFL, DFR, MFC, MFL, MFR, etc.
 */
function mapPositionToEngineFormat(position) {
  console.log('[mapPositionToEngineFormat] Input position:', position);
  
  if (!position || typeof position !== 'string') {
    console.log('[mapPositionToEngineFormat] Invalid input, returning default MFC');
    return 'MFC'; // Default to central midfielder
  }
  
  const normalizedPos = position.trim().toUpperCase().replace(/\s+/g, ''); // Normalize: uppercase, remove spaces

  // Preserve already-engine-formatted positions to avoid accidental side remaps
  // Simple format from engine state updates: GK, DF, DM, MF, AM, FW
  // Concatenated command format: GK, DFC, DFL, DFR, DMC, MFC, MFL, MFR, AMC, AML, AMR, FWC, FWL, FWR
  if (normalizedPos === 'GK') {
    console.log(`[mapPositionToEngineFormat] '${position}' already in engine format -> 'GK'`);
    return 'GK';
  }

  if (/^(DMC|(DF|MF|AM|FW)[LCR])$/.test(normalizedPos)) {
    console.log(`[mapPositionToEngineFormat] '${position}' already in engine format -> '${normalizedPos}'`);
    return normalizedPos;
  }

  if (/^(DF|DM|MF|AM|FW)$/.test(normalizedPos)) {
    const simplePositionMap = {
      DF: 'DFC',
      DM: 'DMC',
      MF: 'MFC',
      AM: 'AMC',
      FW: 'FWC'
    };

    const mappedSimple = simplePositionMap[normalizedPos];
    console.log(`[mapPositionToEngineFormat] '${position}' engine simple format -> '${mappedSimple}'`);
    return mappedSimple;
  }

  const lowerPos = normalizedPos.toLowerCase();

  let corePosition = '';
  let side = 'C';

  // Handle specific positions with explicit sides
  if (lowerPos.includes('left') || lowerPos.includes('lw') || lowerPos.includes('lb') || 
      lowerPos.includes('lm') || lowerPos.includes('lf') || lowerPos.includes('ls') || 
      lowerPos.startsWith('l') && lowerPos.length <= 3) {
    side = 'L';
  } else if (lowerPos.includes('right') || lowerPos.includes('rw') || lowerPos.includes('rb') || 
             lowerPos.includes('rm') || lowerPos.includes('rf') || lowerPos.includes('rs') || 
             lowerPos.startsWith('r') && lowerPos.length <= 3) {
    side = 'R';
  }

  // Map to core positions
  if (lowerPos.includes('gk') || lowerPos.includes('goalkeeper') || lowerPos.includes('keeper')) {
    corePosition = 'GK';
    side = 'C'; // Goalkeepers are always central
  } else if (lowerPos.includes('cb') || lowerPos.includes('centreback') || lowerPos.includes('centredefender') || 
             lowerPos.includes('cd') || (lowerPos.includes('def') && lowerPos.includes('center'))) {
    corePosition = 'DF';
  } else if (lowerPos.includes('lb') || lowerPos.includes('leftback') || 
             lowerPos.includes('rb') || lowerPos.includes('rightback') || 
             lowerPos.includes('fb') || lowerPos.includes('fullback') || 
             lowerPos.includes('wingback') || lowerPos.includes('wb')) {
    corePosition = 'DF';
  } else if (lowerPos.includes('cdm') || lowerPos.includes('dm') || lowerPos.includes('defensivemid') || 
             lowerPos.includes('holdingmid') || lowerPos.includes('anchorman')) {
    corePosition = 'DM';
  } else if (lowerPos.includes('cm') || lowerPos.includes('centralmid') || lowerPos.includes('mid') || 
             lowerPos.includes('lm') || lowerPos.includes('rm') || lowerPos.includes('wm') || 
             lowerPos.includes('wingmid')) {
    corePosition = 'MF';
  } else if (lowerPos.includes('cam') || lowerPos.includes('am') || lowerPos.includes('attackingmid') || 
             lowerPos.includes('playmaker') || lowerPos.includes('trequartista')) {
    corePosition = 'AM';
  } else if (lowerPos.includes('st') || lowerPos.includes('striker') || lowerPos.includes('cf') || 
             lowerPos.includes('centerforward') || lowerPos.includes('lw') || lowerPos.includes('rw') || 
             lowerPos.includes('winger') || lowerPos.includes('lf') || lowerPos.includes('rf') || 
             lowerPos.includes('forward') || lowerPos.includes('fw')) {
    corePosition = 'FW';
  } else {
    // Default fallback
    console.log('[mapPositionToEngineFormat] Unknown position, defaulting to MF');
    corePosition = 'MF';
  }

  // Goalkeepers must be sent as "GK" (never "GKC")
  if (corePosition === 'GK') {
    console.log(`[mapPositionToEngineFormat] Mapped '${position}' -> 'GK' (GK special-case)`);
    return 'GK';
  }

  // Defensive midfielders must be central-only (avoid invalid DML/DMR)
  if (corePosition === 'DM') {
    console.log(`[mapPositionToEngineFormat] Mapped '${position}' -> 'DMC' (DM central-only)`);
    return 'DMC';
  }

  const enginePosition = corePosition + side;
  console.log(`[mapPositionToEngineFormat] Mapped '${position}' -> '${enginePosition}' (${corePosition} + ${side})`);
  return enginePosition;
}

/**
 * Handles player substitutions for both scheduled and in-progress matches
 * 
 * @param {Object} params Configuration parameters
 * @param {string} params.activePlayerId ID of the player to be substituted out
 * @param {string} params.subPlayerId ID of the player to be substituted in
 * @param {string} params.managedTeamId The ID of the team being managed
 * @param {string} params.userTeamSide Either 'home' or 'away'
 * @param {string} params.matchStatus The status of the match ('scheduled', 'in_progress', etc.)
 * @param {number} params.currentSubstitutionsUsed Number of substitutions already used
 * @param {number} params.maxSubstitutions Maximum allowed substitutions
 * @param {Object} params.socketCtx The socket context for in-progress matches
 * @param {Function} params.saveTeamsheetAPI API function to save teamsheet for scheduled matches
 * @param {Object} params.playerDetails Details of both players for updating UI state
 * @param {Object} params.currentTeamsheetData Current teamsheet data with formation, tacticId, etc.
 * @param {string} [params.engineBootAttemptId] Current live command attempt marker
 * @param {Function} params.onStateUpdate Callback for previewing or committing UI state after substitution
 * @param {Function} params.onSuccess Callback for successful substitution
 * @param {Function} params.onError Callback for failed substitution
 * @param {boolean} params.pauseBeforeChange Whether to pause the game before making changes (default: true)
 * @param {boolean} params.resumeAfterChange Whether to resume the game after changes (default: true)
 * @returns {Promise<boolean>} Success status
 */
export async function handleSubstitution({
  activePlayerId,
  subPlayerId,
  managedTeamId,
  userTeamSide,
  matchId,
  matchStatus,
  engineBootAttemptId,
  currentSubstitutionsUsed,
  maxSubstitutions,
  socketCtx,
  saveTeamsheetAPI,
  playerDetails,
  currentTeamsheetData,
  onStateUpdate,
  onSuccess,
  onError,
  pauseBeforeChange = true,
  resumeAfterChange = true,
  overridePosition = null,
  gkEmergency = false
}) {
  try {
    // Find players by IDs
    const { activePlayer, subPlayer } = playerDetails;
    
    // Validate parameters
    if (!activePlayer || !subPlayer || !managedTeamId) {
      onError?.("Could not find players or team context for substitution.");
      return false;
    }
    
    // Validate maximum substitutions for in-progress matches
    if ((matchStatus === 'inProgress' || matchStatus === 'in_progress') && currentSubstitutionsUsed >= maxSubstitutions) {
      onError?.(`Maximum substitutions (${maxSubstitutions}) already used.`);
      return false;
    }

    // Different handling based on match status
    if (matchStatus === 'inProgress' || matchStatus === 'in_progress') {
      // For in-progress matches, send WebSocket command
      if (!socketCtx || !socketCtx.sendUserCommand) {
        onError?.("WebSocket connection not available. Cannot make substitution.");
        return false;
      }

      if (!matchId) {
        onError?.("Cannot make substitution: Match ID missing.");
        return false;
      }

      // Pause game before making changes if requested
      if (pauseBeforeChange) {
        const pauseSuccess = await pauseGame({
          matchId,
          reason: 'substitution',
          engineBootAttemptId,
          socketCtx,
          onSuccess: (message) => onSuccess?.(`${message}. Making substitution...`),
          onError
        });

        if (!pauseSuccess) {
          return false;
        }
      }
      
      // Construct the payload in the format expected by sendUserCommand
      const positionSource = activePlayer.position || activePlayer.currentPosition || activePlayer.formationPlaceId;
      const positionData = overridePosition ?? mapPositionToEngineFormat(positionSource);

      const normalizePositiveInt = (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        const intVal = Math.trunc(num);
        return intVal > 0 ? intVal : null;
      };

      const outPlayerWyId = normalizePositiveInt(activePlayer?.wyId ?? activePlayer?.id);
      const inPlayerWyId = normalizePositiveInt(subPlayer?.wyId ?? subPlayer?.id);

      const payload = {
        command_type: 'SUBSTITUTION',
        // Flattened structure expected by backend; include main fields directly
        command_data: {
          team_id: userTeamSide, // 'home' or 'away'
          out_player_identifier: formatPlayerNameForEngine(activePlayer.name || activePlayer.shortName),
          in_player_identifier: formatPlayerNameForEngine(subPlayer.name || subPlayer.shortName),
          new_position: positionData, // e.g., 'MFC', 'DFL', 'FWR' or 'GK' when override
          // Backend validation requires matchId for routing – include when available
          matchId: matchId,
          ...buildLiveCommandAttemptData(engineBootAttemptId),
          gk_emergency: !!gkEmergency,
          ...(outPlayerWyId ? { out_player_wyId: outPlayerWyId } : {}),
          ...(inPlayerWyId ? { in_player_wyId: inPlayerWyId } : {})
        }
      };

      console.log('[SUBSTITUTION DEBUG] Frontend command summary:', {
        commandType: payload.command_type,
        matchIdPresent: Boolean(payload.command_data.matchId),
        teamSide: payload.command_data.team_id,
        commandDataKeyCount: Object.keys(payload.command_data).length,
        gkEmergency: payload.command_data.gk_emergency,
        clientAttemptSupplied: Boolean(payload.command_data.engineBootAttemptId)
      });
      console.log('[SUBSTITUTION DEBUG] Player selection summary:', {
        activePlayerPresent: Boolean(activePlayer),
        subPlayerPresent: Boolean(subPlayer),
        activePositionPresent: Boolean(activePlayer.position || activePlayer.currentPosition || activePlayer.formationPlaceId),
        resolvedPosition: positionData
      });
      
      // Send the command via socket
      try {
        console.log('[SUBSTITUTION DEBUG] About to call sendUserCommand...');
        const result = await socketCtx.sendUserCommand(payload);
        console.log('[SUBSTITUTION DEBUG] sendUserCommand completed successfully:', {
          resultType: result === null ? 'null' : typeof result,
          resultKeyCount: result && typeof result === 'object' ? Object.keys(result).length : 0
        });
        
        // A busy engine queues the substitution instead of running it, and says
        // so in the ack. That ack means "accepted", not "done": the queued
        // command can still fail when the engine drains it. Committing the swap
        // and the count here would show a lineup the match never had.
        const wasQueued = result?.queued === true;
        const commitSubstitutionToUi = (commandResult) => {
          onSuccess?.('Substitution completed successfully!');

          // Update local state for UI responsiveness
          if (onStateUpdate) {
            onStateUpdate({
              incrementSubstitutions: true,
              // An absolute total, never a delta. See
              // resolveSubstitutionsUsedTotal for why the engine's own number
              // has to win over anything derived from the browser's count.
              substitutionsUsedTotal: resolveSubstitutionsUsedTotal(
                commandResult,
                currentSubstitutionsUsed
              ),
              activePlayer,
              subPlayer
            });
          }
        };

        if (wasQueued) {
          onSuccess?.('Substitution queued. It will happen once the commentary catches up.');
        } else {
          // The immediate ack carries substitutions_made; the queued one cannot,
          // because nothing has run yet.
          commitSubstitutionToUi(result);
        }

        // Resume game after successful change if requested
        if (resumeAfterChange) {
          const resumeSuccess = await resumeGame({
            matchId,
            engineBootAttemptId,
            socketCtx,
            onSuccess,
            onError
          });

          if (!resumeSuccess) {
            return false;
          }
        }

        if (wasQueued) {
          // Watched after the resume, never before it: the engine only drains
          // its queue on the next commentary event, and a paused simulation
          // produces none. Waiting first would guarantee the timeout.
          //
          // Deliberately not awaited. The command is accepted, so the caller
          // can close the sheet now, and correctness does not depend on this
          // result arriving: the backend persists the engine's own total, so
          // the next snapshot corrects the UI within seconds either way.
          watchQueuedSubstitution({
            socketCtx,
            correlationId: result?._correlationId,
            onResolved: commitSubstitutionToUi,
            onRejected: (message) => notifyError(onError, message)
          });
        }

        return true;
      } catch (error) {
        console.error('[SUBSTITUTION DEBUG] sendUserCommand failed:', error);
        const errorMessage = error.message || 'Failed to process substitution request.';
        const errorDetails = getSocketErrorDetails(error);
        
        // If substitution failed but we paused the game, we should resume it
        if (pauseBeforeChange && resumeAfterChange) {
          await resumeGame({
            matchId,
            engineBootAttemptId,
            socketCtx,
            onSuccess: () => {},
            onError: (resumeError) => console.error('Failed to resume after substitution error:', resumeError)
          });
        }
        
        notifyError(onError, errorMessage, errorDetails);
        return false;
      }
    } else {
      // For scheduled matches, save the proposed teamsheet before committing UI state.
      const defaultUpdatedState = {
        updatedActivePlayers: [], 
        updatedSubPlayers: [] 
      };
      let updatedState = defaultUpdatedState;
      const shouldSaveToBackend = Boolean(saveTeamsheetAPI);

      if (shouldSaveToBackend && (!currentTeamsheetData || !currentTeamsheetData.formation || !currentTeamsheetData.tacticId)) {
        onError?.("Missing current teamsheet data. Cannot save substitution.");
        return false;
      }

      if (onStateUpdate) {
        updatedState = onStateUpdate({
          incrementSubstitutions: false,
          activePlayer,
          subPlayer,
          commitState: !shouldSaveToBackend
        }) || defaultUpdatedState;
      }

      // Save to backend using the managedTeamId if API is provided
      if (saveTeamsheetAPI) {
        try {
          // Convert updated state to the correct format for backend
          const updatedPlayers = (updatedState.updatedActivePlayers || []).map(player => ({
            position: player.formationPlaceId || player.position,
            wyId: player.id || player.wyId
          }));

          const updatedSubs = (updatedState.updatedSubPlayers || []).map(player => ({
            position: (player.position && String(player.position).trim().toUpperCase() !== 'SUB')
              ? player.position
              : 'MF',
            wyId: player.id || player.wyId
          }));

          // Construct complete teamsheet payload
          const teamsheetPayload = {
            formation: currentTeamsheetData.formation,
            tacticId: currentTeamsheetData.tacticId,
            players: updatedPlayers,
            subs: updatedSubs
          };
          if (typeof currentTeamsheetData.penaltyTakerWyId === 'number' && Number.isFinite(currentTeamsheetData.penaltyTakerWyId)) {
            const penaltyTakerStillStarting = updatedPlayers.some(
              player => player.wyId === currentTeamsheetData.penaltyTakerWyId
            );
            teamsheetPayload.penaltyTakerWyId = penaltyTakerStillStarting
              ? currentTeamsheetData.penaltyTakerWyId
              : null;
          }

          console.log('[substitutionService] Saving complete teamsheet payload:', teamsheetPayload);

          // Call the API with the complete teamsheet data structure
          await saveTeamsheetAPI({
            teamId: managedTeamId,
            teamsheetData: teamsheetPayload
          });

          if (onStateUpdate) {
            onStateUpdate({
              incrementSubstitutions: false,
              activePlayer,
              subPlayer,
              commitState: true,
              preparedState: updatedState
            });
          }
          
          onSuccess?.('Substitution saved successfully!');
        } catch (error) {
          const errorMessage = getApiErrorMessage(error, 'Failed to save substitution.');
          onError?.(
            isLineupLockedApiError(error)
              ? errorMessage
              : `Failed to save substitution: ${errorMessage}`
          );
          return false;
        }
      } else {
        onSuccess?.('Local substitution applied.');
      }
      
      return true;
    }
  } catch (error) {
    onError?.(error?.response?.data?.message || error?.response?.data?.error || error.message || 'Unknown error during substitution');
    return false;
  }
} 
